import React, { useMemo, useState } from "react";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";

const MultiValueTextField = ({
  disabled = false,
  label,
  onMouseLeave,
  autoFocus,
  onValueChange,
  onArrayChange,
  value,
  isMandatoryField = false,
  placeholder,
  onlyNumbers = false,
  maxlength = 25,
  error,
  errorMessage,
  helperText,
  inputRef,
  name,
  readOnly = false,
  validate,
  input,
}) => {
  const maxLength = onlyNumbers ? 4 : maxlength;

  // State to manage input field value
  const [inputValue, setInputValue] = useState("");

  // State to manage array of stored values
  const [values, setValues] = useState(value || []); // Initialize with props value if provided

  useMemo(() => {
    // Update the selected value if defaultValue changes externally
    setValues(value || []);
  }, [value]);
  useMemo(() => {
    setInputValue(input || "");
  }, [input]);
  const handleInputChange = (e) => {
    let inputValue = e.target.value;
    setInputValue(inputValue);
    onValueChange(inputValue);
  };

  const handleAddValue = () => {
    if (validateInput(inputValue)) {
      setValues([...values, inputValue.trim()]);
      setInputValue(""); // Clear input field after adding value
      onValueChange("");
      // Pass both array of values and inputValue to parent component
      onArrayChange([...values, inputValue.trim()]);
    }
  };

  const validateInput = (input) => {
    if (input.trim() === "") {
      // Handle empty input case
      return false;
    }

    return validate(input); // Default validation success
  };

  const handleRemoveValue = (index) => {
    const updatedValues = [...values];
    updatedValues.splice(index, 1);
    setValues(updatedValues);

    // Pass updated array of values to parent component
    onArrayChange(updatedValues);
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleAddValue();
    }
  };

  const placeholderText =
    placeholder !== undefined
      ? placeholder
      : onlyNumbers
      ? "Enter Number"
      : "Please Enter";

  return (
    <>
      <Typography variant="labelSm" color="text.primary" component="label">
        {label}
        {isMandatoryField && (
          <Typography component="span" color="error">
            *
          </Typography>
        )}
      </Typography>
      {!disabled && (
        <TextField
          autoFocus={autoFocus}
          onMouseLeave={onMouseLeave}
          value={inputValue}
          fullWidth
          variant="outlined"
          placeholder={!readOnly && !disabled ? placeholderText : ""}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
          required={isMandatoryField}
          error={error}
          inputProps={{ maxLength, readOnly }}
          helperText={helperText || errorMessage}
          inputRef={inputRef}
          name={name}
          InputProps={{
            endAdornment: (
              <IconButton onClick={handleAddValue}>
                <AddIcon fontSize="small" />
              </IconButton>
            ),
          }}
        />
      )}

      <Stack spacing={1} mt={1}>
        {values?.map(
          (storedValue, index) =>
            storedValue && (
              <div
                key={index}
                style={{ display: "flex", alignItems: "center" }}
              >
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={storedValue}
                  InputProps={{
                    endAdornment: !disabled && (
                      <IconButton onClick={() => handleRemoveValue(index)}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    ),
                  }}
                  disabled
                />
              </div>
            )
        )}
        {disabled && values.length === 0 && <TextField fullWidth disabled />}
      </Stack>
    </>
  );
};

export default MultiValueTextField;
